<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 套餐分类 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        套餐分类 <span class="text-red-500">*</span>
      </label>
      <select v-model.number="formData.categoryId" required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
        <option value="" disabled>请选择套餐分类</option>
        <option v-for="category in categories" :key="category.id" :value="category.id">
          {{ displayLocalizedText(category.name) }}
        </option>
      </select>
    </div>

    <!-- 套餐代码 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        套餐代码 <span class="text-red-500">*</span>
      </label>
      <input v-model="formData.planCode" type="text" required :disabled="isEdit"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 font-mono dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如: speaking_basic" />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ isEdit ? '套餐代码创建后不可修改' : '使用小写字母和下划线，例如: speaking_basic, writing_pro' }}
      </p>
    </div>

    <!-- 套餐名称 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        套餐名称 <span class="text-red-500">*</span>
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input v-model="formData.name.zh" type="text" required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
          placeholder="中文，例如: 口语基础套餐" />
        <input v-model="formData.name.en" type="text"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
          placeholder="English, e.g. Speaking Basic Plan" />
      </div>
    </div>

    <!-- 套餐描述 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">套餐描述</label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <textarea v-model="formData.description.zh" rows="3"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none dark:bg-gray-700 dark:text-gray-200"
          placeholder="中文，例如: 适合初学者的口语练习套餐" />
        <textarea v-model="formData.description.en" rows="3"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none dark:bg-gray-700 dark:text-gray-200"
          placeholder="English description" />
      </div>
    </div>

    <!-- 显示顺序 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示顺序</label>
      <input v-model.number="formData.displayOrder" type="number" min="0"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        placeholder="0" />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">数字越小越靠前，默认为 0</p>
    </div>

    <!-- 状态选项 -->
    <div class="space-y-3">
      <div class="flex items-center">
        <input v-model="formData.isActive" type="checkbox" id="isActive"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded" />
        <label for="isActive" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          启用套餐
        </label>
      </div>
      <div class="flex items-center">
        <input v-model="formData.isHidden" type="checkbox" id="isHidden"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded" />
        <label for="isHidden" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          隐藏套餐（不在用户前端展示）
        </label>
      </div>
    </div>

    <!-- 按钮 -->
    <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
      <button type="button" @click="handleCancel"
        class="cancel-btn">
        取消
      </button>
      <button type="submit" class="primary-btn">
        {{ isEdit ? '更新' : '创建' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { Plan, CreatePlanRequest, UpdatePlanRequest } from '@/types/membership/plan'
import type { PlanCategory } from '@/types/membership/category'
import {
  displayLocalizedText,
  emptyLocalizedText,
  normalizeLocalizedText,
  type LocalizedText,
} from '@/types/membership/localized'

type PlanFormData = Omit<CreatePlanRequest, 'description'> & {
  description: LocalizedText
}

/**
 * Props
 */
interface Props {
  /** 套餐（编辑模式时传入） */
  plan?: Plan
  /** 套餐分类列表 */
  categories: PlanCategory[]
  /** 是否为编辑模式 */
  isEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
})

/**
 * Emits
 */
const emit = defineEmits<{
  /** 提交表单 */
  submit: [data: CreatePlanRequest | UpdatePlanRequest]
  /** 取消操作 */
  cancel: []
}>()

/**
 * 表单数据
 */
const formData = ref<PlanFormData>({
  categoryId: 0,
  planCode: '',
  name: emptyLocalizedText(),
  description: emptyLocalizedText(),
  isActive: true,
  isHidden: false,
  displayOrder: 0,
})

/**
 * 监听 plan prop 变化，用于编辑模式
 */
watch(
  () => props.plan,
  (newPlan) => {
    if (newPlan) {
      formData.value = {
        categoryId: newPlan.categoryId,
        planCode: newPlan.planCode,
        name: normalizeLocalizedText(newPlan.name),
        description: normalizeLocalizedText(newPlan.description),
        isActive: newPlan.isActive,
        isHidden: newPlan.isHidden,
        displayOrder: newPlan.displayOrder,
      }
    }
  },
  { immediate: true }
)

/**
 * 处理表单提交
 */
const handleSubmit = () => {
  if (props.isEdit) {
    // 编辑模式：只提交可修改的字段
    const updateData: UpdatePlanRequest = {
      categoryId: formData.value.categoryId,
      name: formData.value.name,
      description: formData.value.description,
      isActive: formData.value.isActive,
      isHidden: formData.value.isHidden,
      displayOrder: formData.value.displayOrder,
    }
    emit('submit', updateData)
  } else {
    // 创建模式：提交所有字段
    emit('submit', formData.value)
  }
}

/**
 * 处理取消操作
 */
const handleCancel = () => {
  emit('cancel')
}
</script>

<style scoped>
/* 可选：添加自定义样式 */
</style>
